import {
  AppChannel,
  ChannelOption,
  PaymentMethod,
  PaymentModuleConfig,
  PaymentPlan,
  PaymentProviderId,
  paymentOptionKey,
} from '../types';

/**
 * ─── Valeurs par défaut du module ────────────────────────────────────────────
 *
 * `enabled: false` par défaut : l'application se comporte exactement comme
 * avant l'introduction du module (aucun écran, aucun blocage). L'activation
 * se fait depuis /admin/payments.
 *
 * CONTRAT BACKEND (à brancher plus tard) :
 * la config et les plans devront être servis par l'API — ex.
 * `GET /public/config/payments` → { config: PaymentModuleConfig, plans: PaymentPlan[] }
 * et administrés via `PATCH /admin/payments/config`. En attendant, ils vivent
 * dans le store persisté localement avec ces valeurs par défaut.
 */

export const DEFAULT_PAYMENT_CONFIG: PaymentModuleConfig = {
  enabled: false,
  maintenanceMode: false,
  providers: {
    [PaymentProviderId.FLEXPAY]: true,
    [PaymentProviderId.MPESA]: false, // en attente de la documentation M-Pesa
    [PaymentProviderId.ORANGE]: false, // en attente de la documentation Orange Money
    [PaymentProviderId.MOCK]: true,
  },
  // Toutes les options (moyen × fournisseur) sont activées par défaut ; l'admin
  // en coupe une individuellement le temps d'une maintenance (voir
  // /admin/payments). Carte bancaire FlexPay et carte bancaire M-Pesa sont deux
  // options distinctes.
  optionEnabled: {
    [paymentOptionKey(PaymentMethod.BANK_CARD, PaymentProviderId.FLEXPAY)]: true,
    [paymentOptionKey(PaymentMethod.MOBILE_MONEY, PaymentProviderId.FLEXPAY)]: true,
    [paymentOptionKey(PaymentMethod.BANK_CARD, PaymentProviderId.MPESA)]: true,
    [paymentOptionKey(PaymentMethod.MPESA, PaymentProviderId.MPESA)]: true,
    [paymentOptionKey(PaymentMethod.ORANGE_MONEY, PaymentProviderId.ORANGE)]: true,
  },
  defaultProviderId: PaymentProviderId.FLEXPAY,
  mockMode: true, // aucun backend branché → Mock par défaut
  verificationTimeoutSeconds: 120,
};

/**
 * ─── Catalogue des options par canal (source de vérité) ──────────────────────
 *
 * Chaque canal propose une liste ORDONNÉE d'options (méthode → provider). Le
 * provider effectif dépend du couple (canal, méthode) : la carte bancaire
 * passe par FlexPay sur le web mais par M-Pesa dans la Super App M-Pesa.
 *
 * Le checkout n'affiche que les options du canal courant dont la méthode est
 * activée en admin. C'est ici — et nulle part ailleurs — qu'on ajoute/retire
 * une combinaison canal × moyen de paiement.
 */
export const CHANNEL_OPTIONS: Record<AppChannel, ChannelOption[]> = {
  // Monde (carte) + RDC (mobile money), via l'agrégateur FlexPay.
  [AppChannel.WEB]: [
    { method: PaymentMethod.BANK_CARD, provider: PaymentProviderId.FLEXPAY },
    { method: PaymentMethod.MOBILE_MONEY, provider: PaymentProviderId.FLEXPAY },
  ],
  // Maxit impose Orange Money exclusivement.
  [AppChannel.MAXIT]: [
    { method: PaymentMethod.ORANGE_MONEY, provider: PaymentProviderId.ORANGE },
  ],
  // M-Pesa impose sa carte Visa + le paiement M-Pesa.
  [AppChannel.MPESA_SUPER_APP]: [
    { method: PaymentMethod.BANK_CARD, provider: PaymentProviderId.MPESA },
    { method: PaymentMethod.MPESA, provider: PaymentProviderId.MPESA },
  ],
};

/**
 * Liste DÉDUPLIQUÉE des options de paiement (couple moyen × fournisseur) telles
 * qu'elles existent dans `CHANNEL_OPTIONS`, avec les canaux où chacune apparaît.
 * C'est la source de l'écran admin « Moyens de paiement » : une ligne (donc un
 * kill-switch) par option — carte FlexPay et carte M-Pesa sont deux lignes.
 */
export const ALL_PAYMENT_OPTIONS: {
  method: PaymentMethod;
  provider: PaymentProviderId;
  channels: AppChannel[];
}[] = (() => {
  const seen = new Map<string, { method: PaymentMethod; provider: PaymentProviderId; channels: AppChannel[] }>();
  (Object.keys(CHANNEL_OPTIONS) as AppChannel[]).forEach((ch) => {
    CHANNEL_OPTIONS[ch].forEach((o) => {
      const key = paymentOptionKey(o.method, o.provider);
      const entry = seen.get(key) ?? { method: o.method, provider: o.provider, channels: [] };
      if (!entry.channels.includes(ch)) entry.channels.push(ch);
      seen.set(key, entry);
    });
  });
  return [...seen.values()];
})();

/**
 * Migration ascendante : convertit l'ancien blob `config.methods` (kill-switch
 * par MOYEN global) en overrides `optionEnabled` par OPTION. Un moyen désactivé
 * jadis coupe toutes ses options (tous fournisseurs). Sert à préserver un
 * réglage admin existant après la bascule vers le kill-switch par option.
 */
export function legacyMethodsToOptionEnabled(
  methods?: Record<string, { enabled?: boolean }>,
): Record<string, boolean> {
  if (!methods || typeof methods !== 'object') return {};
  const out: Record<string, boolean> = {};
  for (const { method, provider } of ALL_PAYMENT_OPTIONS) {
    if (methods[method] && methods[method].enabled === false) {
      out[paymentOptionKey(method, provider)] = false;
    }
  }
  return out;
}

/**
 * Libellés d'affichage des moyens de paiement (texte pur, réutilisable hors
 * React : facture, historique…). Les icônes sont mappées dans le checkout.
 * `context` situe l'option pour l'écran admin.
 */
export const PAYMENT_METHOD_META: Record<
  PaymentMethod,
  { label: string; hint: string; context: string }
> = {
  [PaymentMethod.BANK_CARD]: {
    label: 'Carte bancaire',
    hint: 'Visa · Mastercard',
    context: 'Monde / M-Pesa',
  },
  [PaymentMethod.MOBILE_MONEY]: {
    label: 'Mobile Money',
    hint: 'Airtel · Orange · M-Pesa · Africell (RDC)',
    context: 'Monde (RDC)',
  },
  [PaymentMethod.ORANGE_MONEY]: {
    label: 'Orange Money',
    hint: 'Validation sur votre téléphone',
    context: 'Maxit',
  },
  [PaymentMethod.MPESA]: {
    label: 'M-Pesa',
    hint: 'Validation par code PIN',
    context: 'M-Pesa',
  },
};

// Les limites (entitlements) sont la SOURCE DE VÉRITÉ : le QuotaGate les
// applique réellement, et PlanCard affiche les lignes d'avantages dérivées.
// `features` ne contient que les avantages additionnels (non applicables).
export const DEFAULT_PLANS: PaymentPlan[] = [
  {
    id: 'pass-7d',
    name: 'Pass 7 jours',
    description: 'Testez sans engagement — un seul paiement',
    price: 2,
    currency: 'USD',
    periodDays: 7,
    entitlements: {
      dailyPracticeLimit: null, // pratique illimitée
      dailySimulationLimit: 2, // 2 simulations complètes par jour
    },
    features: [
      'Accès à tous les examens',
      'Idéal juste avant un examen',
    ],
    isActive: true,
  },
  {
    id: 'standard-monthly',
    name: 'Standard',
    description: 'Pour préparer un examen à votre rythme',
    price: 5,
    currency: 'USD',
    periodDays: 30,
    entitlements: {
      dailyPracticeLimit: null, // pratique illimitée
      dailySimulationLimit: 3, // 3 simulations complètes par jour
    },
    features: [
      'Accès à tous les examens',
      'Statistiques de progression',
    ],
    isActive: true,
  },
  {
    id: 'premium-monthly',
    name: 'Premium',
    description: 'Pour une préparation intensive',
    price: 9,
    currency: 'USD',
    periodDays: 30,
    entitlements: {
      dailyPracticeLimit: null,
      dailySimulationLimit: null, // tout illimité
    },
    features: [
      'Accès à tous les examens',
      'Explications détaillées des réponses',
      'Classement et succès exclusifs',
      'Support prioritaire',
    ],
    highlighted: true,
    isActive: true,
  },
];
